import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { MoreHorizontal, Plus, AlertTriangle, CalendarDays, Flag, CheckSquare, MessageSquare, Layers, Link as LinkIcon, FileSignature } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import BoardCardModal from "@/components/BoardCardModal";

const initials = (name) => (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

const LABEL_COLORS = {
  green: "#61BD4F", yellow: "#F2D600", orange: "#FF9F1A",
  red: "#EB5A46", purple: "#C377E0", blue: "#0079BF",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const URGENCY_STYLE = {
  overdue: { label: "Überfällig", className: "bg-red-100 text-red-700 border-red-200" },
  today: { label: "Heute fällig", className: "bg-orange-100 text-orange-700 border-orange-200" },
  soon: { label: "Bald fällig", className: "bg-amber-100 text-amber-700 border-amber-200" },
};

const computeUrgency = (dueDate, status) => {
  if (status === "done" || !dueDate) return "none";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${dueDate}T00:00:00`);
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays < 0) return "overdue";
  if (diffDays === 0) return "today";
  if (diffDays <= 2) return "soon";
  return "none";
};

export default function KanbanBoard({ boardId, initialCardId }) {
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newListTitle, setNewListTitle] = useState("");
  const [addingList, setAddingList] = useState(false);
  const [addingCardFor, setAddingCardFor] = useState(null);
  const [newCardTitle, setNewCardTitle] = useState("");
  const [templatePickerFor, setTemplatePickerFor] = useState(null);
  const [activeCardId, setActiveCardId] = useState(initialCardId || null);

  const load = async () => {
    try {
      const [b, t, tpl] = await Promise.all([
        api.get(`/boards/${boardId}`),
        api.get("/boards/team-members"),
        api.get("/boards/templates", { params: { board_id: boardId } }),
      ]);
      setData(b.data);
      setTeam(t.data);
      setTemplates(tpl.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); load(); }, [boardId]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamById = useMemo(() => Object.fromEntries(team.map((m) => [m.user_id, m])), [team]);
  const canManageLists = !!data?.can_manage_lists;

  const persistCardOrder = async (listId, cardIds) => {
    try {
      await api.post(`/boards/lists/${listId}/reorder`, { card_ids: cardIds });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      load();
    }
  };

  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (type === "COLUMN") {
      const lists = Array.from(data.lists);
      const [moved] = lists.splice(source.index, 1);
      lists.splice(destination.index, 0, moved);
      setData({ ...data, lists });
      api.post(`/boards/${boardId}/lists/reorder`, { list_ids: lists.map((l) => l.list_id) }).catch(() => load());
      return;
    }
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const lists = data.lists.map((l) => ({ ...l, cards: Array.from(l.cards) }));
    const srcList = lists.find((l) => l.list_id === source.droppableId);
    const destList = lists.find((l) => l.list_id === destination.droppableId);
    const [moved] = srcList.cards.splice(source.index, 1);
    if (destList.is_done_list && moved.status !== "done") {
      moved.status = "done";
      moved.urgency = "none";
    } else if (!destList.is_done_list && moved.status === "done") {
      moved.status = "open";
      moved.urgency = computeUrgency(moved.due_date, "open");
    }
    destList.cards.splice(destination.index, 0, moved);
    setData({ ...data, lists });
    persistCardOrder(destList.list_id, destList.cards.map((c) => c.card_id));
  };

  const createList = async () => {
    if (!newListTitle.trim()) { setAddingList(false); return; }
    try {
      await api.post(`/boards/${boardId}/lists`, { title: newListTitle.trim() });
      setNewListTitle("");
      setAddingList(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const createCard = async (listId) => {
    if (!newCardTitle.trim()) { setAddingCardFor(null); return; }
    try {
      await api.post(`/boards/lists/${listId}/cards`, { title: newCardTitle.trim() });
      setNewCardTitle("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const createCardFromTemplate = async (listId, templateId) => {
    try {
      await api.post(`/boards/lists/${listId}/cards-from-template/${templateId}`);
      setTemplatePickerFor(null);
      toast.success("Karte aus Vorlage erstellt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const renameList = async (list) => {
    const title = window.prompt("Neuer Titel für die Liste:", list.title);
    if (!title || !title.trim()) return;
    try {
      await api.patch(`/boards/lists/${list.list_id}`, { title: title.trim() });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggleDoneList = async (list) => {
    try {
      await api.patch(`/boards/lists/${list.list_id}`, { is_done_list: !list.is_done_list });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const deleteList = async (list) => {
    if (!window.confirm(`Liste "${list.title}" inkl. aller Karten wirklich löschen?`)) return;
    try {
      await api.delete(`/boards/lists/${list.list_id}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (loading) return <div className="text-xs font-bold uppercase tracking-overline text-slate-500 py-8">Lädt Board…</div>;
  if (!data) return null;

  return (
    <div data-testid={`kanban-board-${boardId}`}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="board" direction="horizontal" type="COLUMN">
          {(providedBoard) => (
            <div
              ref={providedBoard.innerRef}
              {...providedBoard.droppableProps}
              className="flex gap-4 overflow-x-auto pb-4 items-start"
            >
              {data.lists.map((list, listIndex) => (
                <Draggable draggableId={list.list_id} index={listIndex} key={list.list_id} isDragDisabled={!canManageLists}>
                  {(providedCol) => (
                    <div
                      ref={providedCol.innerRef}
                      {...providedCol.draggableProps}
                      className="bg-slate-50 border border-slate-200 w-72 shrink-0 flex flex-col max-h-[75vh]"
                      data-testid={`board-list-${list.list_id}`}
                    >
                      <div
                        {...providedCol.dragHandleProps}
                        className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold text-slate-800 truncate" data-testid={`board-list-title-${list.list_id}`}>
                            {list.title}
                          </span>
                          {list.is_done_list && (
                            <span className="text-[9px] font-bold uppercase tracking-overline bg-emerald-100 text-emerald-700 px-1.5 py-0.5 shrink-0">
                              Erledigt
                            </span>
                          )}
                          <span className="text-xs text-slate-400 shrink-0">{list.cards.length}</span>
                        </div>
                        {canManageLists && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`board-list-menu-${list.list_id}`} className="text-slate-400 hover:text-slate-800 p-1">
                                <MoreHorizontal size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-white">
                              <DropdownMenuItem data-testid={`board-list-rename-${list.list_id}`} onClick={() => renameList(list)}>Umbenennen</DropdownMenuItem>
                              <DropdownMenuItem data-testid={`board-list-toggle-done-${list.list_id}`} onClick={() => toggleDoneList(list)}>
                                {list.is_done_list ? "Als 'Erledigt'-Liste entfernen" : "Als 'Erledigt'-Liste markieren"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem data-testid={`board-list-delete-${list.list_id}`} onClick={() => deleteList(list)} className="text-red-600">
                                Liste löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>

                      <Droppable droppableId={list.list_id} type="CARD">
                        {(providedList, snapshot) => (
                          <div
                            ref={providedList.innerRef}
                            {...providedList.droppableProps}
                            className={`flex-1 overflow-y-auto p-2 space-y-2 min-h-[40px] ${snapshot.isDraggingOver ? "bg-slate-100" : ""}`}
                          >
                            {list.cards.map((card, cardIndex) => {
                              const urgency = URGENCY_STYLE[card.urgency];
                              return (
                                <Draggable draggableId={card.card_id} index={cardIndex} key={card.card_id}>
                                  {(providedCard, cardSnapshot) => (
                                    <div
                                      ref={providedCard.innerRef}
                                      {...providedCard.draggableProps}
                                      {...providedCard.dragHandleProps}
                                      onClick={() => setActiveCardId(card.card_id)}
                                      data-testid={`board-card-${card.card_id}`}
                                      className={`bg-white border p-0 text-sm cursor-pointer transition-colors overflow-hidden ${
                                        card.card_type === "contract_approval"
                                          ? "border-[#00CCC9] ring-2 ring-[#00CCC9]/40 shadow-[0_0_14px_rgba(0,204,201,0.35)] hover:ring-[#00CCC9]/60"
                                          : "border-slate-200 hover:border-slate-400"
                                      } ${cardSnapshot.isDragging ? "shadow-lg ring-1 ring-slate-300" : ""}`}
                                    >
                                      {(card.labels || []).length > 0 && (
                                        <div className="flex gap-1 px-3 pt-2.5">
                                          {card.labels.map((l) => (
                                            <span key={l.id} className="h-1.5 w-8" style={{ backgroundColor: LABEL_COLORS[l.color] || "#0079BF" }} title={l.name} />
                                          ))}
                                        </div>
                                      )}
                                      <div className="p-3 pt-2">
                                      {card.card_type === "contract_approval" && (
                                        <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-overline text-[#0A6E6C] bg-[#00CCC9]/15 px-1.5 py-0.5 mb-1.5" data-testid={`board-card-contract-badge-${card.card_id}`}>
                                          <FileSignature size={9} strokeWidth={2} /> Vertrag zur Freigabe
                                        </div>
                                      )}
                                      <div className={`leading-snug ${card.status === "done" && !list.is_done_list ? "text-slate-400 line-through" : "text-slate-800"}`}>
                                        {card.status === "done" && !list.is_done_list && (
                                          <CheckSquare size={11} className="inline mr-1 -mt-0.5 text-emerald-500" data-testid={`board-card-done-elsewhere-${card.card_id}`} />
                                        )}
                                        {card.title}
                                      </div>
                                      {card.client_id && (
                                        <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-overline text-slate-500 mt-1.5" data-testid={`board-card-client-badge-${card.card_id}`}>
                                          <LinkIcon size={9} strokeWidth={2} /> {card.client_name || "Kunde verknüpft"}
                                        </div>
                                      )}
                                      {card.linked_subtask_id && (
                                        <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-overline text-[#00807E] mt-1.5" data-testid={`board-card-goal-flag-${card.card_id}`}>
                                          <Flag size={9} strokeWidth={2} /> Wochenziel
                                        </div>
                                      )}
                                      {card.is_mirror && (
                                        <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-overline text-slate-500 mt-1.5 bg-slate-100 px-1.5 py-0.5" data-testid={`board-card-mirror-badge-${card.card_id}`}>
                                          <LinkIcon size={9} strokeWidth={2} /> Von: {card.source_board_label || "anderes Board"}
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                                        <div className="flex items-center gap-2">
                                          {card.checklist_total > 0 && (
                                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${card.checklist_done === card.checklist_total ? "text-emerald-600" : "text-slate-500"}`} data-testid={`board-card-checklist-badge-${card.card_id}`}>
                                              <CheckSquare size={11} /> {card.checklist_done}/{card.checklist_total}
                                            </span>
                                          )}
                                          {card.comment_count > 0 && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500" data-testid={`board-card-comment-badge-${card.card_id}`}>
                                              <MessageSquare size={11} /> {card.comment_count}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                          {(card.assignee_ids || []).map((uid) => (
                                            <span
                                              key={uid}
                                              title={teamById[uid]?.name || uid}
                                              className="w-5 h-5 rounded-full bg-slate-800 text-white text-[9px] font-bold flex items-center justify-center"
                                            >
                                              {initials(teamById[uid]?.name)}
                                            </span>
                                          ))}
                                        </div>
                                        {card.due_date && (
                                          <span
                                            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 border ${
                                              urgency ? urgency.className : "bg-slate-50 text-slate-500 border-slate-200"
                                            }`}
                                            title={urgency?.label}
                                            data-testid={`board-card-due-badge-${card.card_id}`}
                                          >
                                            {urgency?.label === "Überfällig" ? <AlertTriangle size={10} /> : <CalendarDays size={10} />}
                                            {urgency ? `${urgency.label} · ${fmtDate(card.due_date)}` : fmtDate(card.due_date)}
                                          </span>
                                        )}
                                      </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {providedList.placeholder}
                          </div>
                        )}
                      </Droppable>

                      <div className="p-2 border-t border-slate-100">
                        {addingCardFor === list.list_id ? (
                          <div className="space-y-2">
                            <textarea
                              autoFocus
                              data-testid={`board-new-card-input-${list.list_id}`}
                              value={newCardTitle}
                              onChange={(e) => setNewCardTitle(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); createCard(list.list_id); } }}
                              rows={2}
                              placeholder="Titel der Karte…"
                              className="w-full border border-slate-300 p-2 text-sm focus:outline-none focus:border-slate-500"
                            />
                            <div className="flex gap-2">
                              <button
                                data-testid={`board-new-card-save-${list.list_id}`}
                                onClick={() => createCard(list.list_id)}
                                className="bg-slate-900 text-white text-xs font-medium px-3 py-1.5"
                              >
                                Hinzufügen
                              </button>
                              <button
                                onClick={() => { setAddingCardFor(null); setNewCardTitle(""); }}
                                className="text-xs text-slate-500 px-2"
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              data-testid={`board-add-card-button-${list.list_id}`}
                              onClick={() => setAddingCardFor(list.list_id)}
                              className="flex-1 flex items-center gap-2 text-xs font-medium text-slate-500 hover:bg-slate-100 px-2 py-2"
                            >
                              <Plus size={14} /> Eine Karte hinzufügen
                            </button>
                            {templates.length > 0 && (
                              <DropdownMenu open={templatePickerFor === list.list_id} onOpenChange={(o) => setTemplatePickerFor(o ? list.list_id : null)}>
                                <DropdownMenuTrigger asChild>
                                  <button data-testid={`board-add-from-template-button-${list.list_id}`} title="Karte aus Vorlage erstellen"
                                    className="p-2 text-slate-500 hover:bg-slate-100 shrink-0">
                                    <Layers size={14} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-white w-64" data-testid={`template-picker-${list.list_id}`}>
                                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 px-2 py-1.5">Karte aus Vorlage erstellen</div>
                                  {templates.map((tpl) => (
                                    <DropdownMenuItem key={tpl.template_id} data-testid={`template-picker-item-${tpl.template_id}`} onClick={() => createCardFromTemplate(list.list_id, tpl.template_id)}>
                                      {tpl.title}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {providedBoard.placeholder}

              {canManageLists && (
                <div className="w-72 shrink-0">
                  {addingList ? (
                    <div className="bg-slate-50 border border-slate-200 p-2 space-y-2">
                      <input
                        autoFocus
                        data-testid="board-new-list-input"
                        value={newListTitle}
                        onChange={(e) => setNewListTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") createList(); }}
                        placeholder="Titel der Liste…"
                        className="w-full border border-slate-300 p-2 text-sm focus:outline-none focus:border-slate-500"
                      />
                      <div className="flex gap-2">
                        <button data-testid="board-new-list-save" onClick={createList} className="bg-slate-900 text-white text-xs font-medium px-3 py-1.5">
                          Liste hinzufügen
                        </button>
                        <button onClick={() => { setAddingList(false); setNewListTitle(""); }} className="text-xs text-slate-500 px-2">
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid="board-add-list-button"
                      onClick={() => setAddingList(true)}
                      className="w-full flex items-center gap-2 text-sm font-medium text-slate-500 hover:bg-slate-100 px-3 py-2.5 border border-dashed border-slate-300"
                    >
                      <Plus size={16} /> Weitere Liste
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <BoardCardModal
        open={!!activeCardId}
        onOpenChange={(o) => !o && setActiveCardId(null)}
        cardId={activeCardId}
        teamMembers={team}
        onChanged={load}
      />
    </div>
  );
}
